import type { LegalBlock, LegalSection, PrivacyProcessors } from "@serophin/shared";
import { CONTACT_US } from "./app";

export const PRIVACY_PROCESSORS: PrivacyProcessors = {
  intro: "We do not sell your personal information. We work with a few providers, each only for its part of the Services:",
  list: [
    { name: "Amazon Web Services", purpose: "Hosts the Services and sends our emails." },
    { name: "Cloudflare", purpose: "Runs our DNS and stores the article audio, which your browser or app fetches from it." },
    { name: "Stripe", purpose: "Processes optional tips." },
    { name: "Google", purpose: "Signs you in when you choose it, and through Google Workspace holds the messages you send to support@serophin.com." },
    { name: "Apple", purpose: "Signs you in when you choose it; our sign-in page loads Apple's sign-in script." },
    { name: "Unsplash", purpose: "Article and author photos are loaded from Unsplash, which sees your IP address when your browser fetches them." },
  ],
  notes: [
    "Our fonts are served from our own servers. These providers are contractually required to protect your data and only use it for the services they provide.",
  ],
};

export const PRIVACY_SHARING: LegalSection = {
  heading: "How We Share Your Information",
  blocks: [
    "Apart from the providers above, we share data only in limited circumstances:",
    {
      list: [
        "**Legal Requirements:** We may disclose information if required by law, court order, or in response to a valid legal request (e.g., a subpoena).",
        "**Business Transfers:** If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that deal. We will notify you of any such change in control.",
        '**Aggregated Data:** We may share anonymized, aggregated statistics that cannot identify individual users (e.g., "70% of our users are interested in AI").',
      ],
    },
  ],
};

export const PRIVACY_AI: LegalSection = {
  heading: "AI-Generated Content and Data Use",
  blocks: [
    "Our articles are generated by AI. We want to be transparent about this:",
    {
      list: [
        "**No Training on Your Data:** We do not use your reading activity or personal information to train external, third-party AI models.",
        "**Internal Improvement Only:** Your reading patterns help us improve our internal content recommendation system, but this happens on our platform with strict privacy controls.",
      ],
    },
  ],
};

export const PRIVACY_INTERNATIONAL: LegalSection = {
  heading: "International Users",
  blocks: [
    "Our Services are run from Australia and hosted with Amazon Web Services in the United States (Oregon), and our providers above also handle data outside Australia, mostly in the United States. If you live in the European Union or the United Kingdom, these transfers rely on the safeguards those providers offer, such as the European Commission's standard contractual clauses and the UK's equivalent.",
  ],
};

export const PRIVACY_RETENTION: LegalBlock[] = [
  "We retain your personal data for as long as your account is active. After you delete your account, we will permanently delete your data within 90 days, except for information we are legally required to keep (e.g., payment invoices for tax purposes).",
];

export const PRIVACY_RIGHTS: LegalBlock[] = [
  "We believe you should have control over your data. You have the following rights:",
  {
    list: [
      `**Access and Export:** You can ${CONTACT_US} to request a copy of your personal data at any time, and we will answer within one month.`,
      `**Deletion:** You can delete your account at any time from your account settings. If you need assistance, you may also ${CONTACT_US}.`,
      '**Optional Emails:** Digests and other optional emails are off until you turn them on in your email preferences. Every digest has an "Unsubscribe" link (and a one-click unsubscribe your mail app can show) that turns that email off at once, without signing in, and you can turn all of them off in your email preferences. You will still receive essential account emails, such as sign-in codes.',
      "**Cookie Controls:** We only use essential cookies required for the Service to function.",
    ],
  },
  { subheading: "Your Regional Privacy Rights" },
  "Depending on your location, you may have additional rights under laws like the GDPR (Europe) or CCPA (California). These may include:",
  {
    list: [
      "**Right to Rectification:** The right to correct inaccurate personal data we hold about you.",
      "**Right to Object:** The right to object to our processing of your data for certain purposes.",
      `**California Rights:** The right to opt out of the "sale" or "sharing" of your personal information (as defined by California law). We do not sell or share your personal information, so there is nothing to opt out of; you can still ${CONTACT_US} to exercise this or any other right.`,
      "**Right to Complain:** The right to lodge a complaint with your local data protection authority.",
    ],
  },
];

export const PRIVACY_SECURITY_AND_CHILDREN: LegalSection[] = [
  {
    heading: "Data Security",
    blocks: [
      "We implement industry-standard technical and organizational security measures to protect your information, including encryption in transit and at rest and secure authentication. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.",
    ],
  },
  {
    heading: "Children's Privacy",
    blocks: [
      `!tldr is not directed to children under 13 (or 16 in certain European regions). We do not knowingly collect personal information from children. If you believe we have collected information from a child, please ${CONTACT_US} immediately so we can delete it.`,
    ],
  },
];
